import { useLoader } from '@/contexts/useLoader';
import { fetchCurrentUser, setCurrentUser } from '@/features/users/store/slice';
import { AppRoutes } from '@/routes';
import { getAccessToken, setLoaderContext } from '@/shared/api';
import { authApi } from '@/shared/api/auth';
import { useInactivityTimeout } from '@/shared/hooks/useInactivityTimeout';
import type { CurrentUser } from '@/shared/types/user';
import { parseJson } from '@/shared/utils/json';
import type { AppDispatch, RootState } from '@/store';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

const SESSION_KEY = 'currentUser';

export const AppContent: React.FC = () => {
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.users);
  const [init, setInit] = React.useState(false);

  useInactivityTimeout();

  React.useEffect(() => {
    setLoaderContext({ showLoader, hideLoader });
  }, [showLoader, hideLoader]);

  React.useEffect(() => {
    const initAuth = async () => {
      const accessToken = getAccessToken();

      if (accessToken) {
        const cached = sessionStorage.getItem(SESSION_KEY);
        if (cached) {
          try {
            const user = parseJson<CurrentUser>(cached);
            dispatch(setCurrentUser(user));
            setInit(true);
            return;
          } catch {
            sessionStorage.removeItem(SESSION_KEY);
          }
        }

        try {
          const user = await dispatch(fetchCurrentUser()).unwrap();
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
        } catch (error) {
          console.debug('Auth initialization failed:', error);
        } finally {
          setInit(true);
        }
      } else {
        // No access token - check if we have a refresh token
        try {
          await authApi.refresh();
          const user = await dispatch(fetchCurrentUser()).unwrap();
          sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
        } catch (error) {
          console.debug('Auth initialization failed:', error);
        } finally {
          setInit(true);
        }
      }
    };

    void initAuth();
  }, [dispatch]);

  if (!init || loading) {
    return <div style={{ minHeight: 200 }} />;
  }

  return <AppRoutes />;
};
